import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useQuests } from '../hooks/useQuests';
import ChestIcon from '../components/ChestIcon';
import type { DailyQuest } from '../types/quest';

// Helpers for Persian digits consistent with ChallengesPage
const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
const toPersianNumber = (n: number | string) => toPersianDigits(String(n));

// Module-level variable to track animation state across navigations (resets on reload)
let hasShownAnimation = false;

const DailyQuests: React.FC = () => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(24 * 60); // 24 hours in minutes
  const { quests: dailyQuests, isLoading, timeRemainingMinutes } = useQuests();

  // Capture the animation state for this render cycle
  const [shouldAnimate] = useState(!hasShownAnimation);

  useEffect(() => {
    // Mark as shown for future visits in this session
    hasShownAnimation = true;
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Sync local state with server-provided remaining time
  useEffect(() => {
    if (typeof timeRemainingMinutes === 'number') {
      setTimeRemaining(timeRemainingMinutes);
    }
  }, [timeRemainingMinutes]);

  // Sequential progress animation (like ChallengesPage)
  const targets = useMemo(
    () => dailyQuests.map(q => Math.min(100, Math.max(0, (q.target > 0 ? (q.progress / q.target) * 100 : 0))))
    , [dailyQuests]
  );
  const [widths, setWidths] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;

    const animateIndex = async (idx: number): Promise<void> => {
      if (cancelled || idx >= dailyQuests.length) return;
      const target = targets[idx] || 0;
      const durationMs = 625; // 0.625s like ChallengesPage
      const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const start = performance.now();

      await new Promise<void>((resolve) => {
        const step = (now: number) => {
          if (cancelled) return resolve();
          const elapsed = now - start;
          const t = Math.min(1, elapsed / durationMs);
          const eased = easeInOut(t);
          const next = target * eased;
          setWidths(prev => {
            const base = prev.length === dailyQuests.length ? prev : new Array(dailyQuests.length).fill(0);
            const copy = [...base];
            copy[idx] = next;
            return copy;
          });
          if (t < 1) {
            requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        requestAnimationFrame(step);
      });

      await animateIndex(idx + 1);
    };

    if (dailyQuests.length > 0) {
      if (shouldAnimate) {
        // Initialize widths once right before starting animation to avoid flicker
        setWidths(new Array(dailyQuests.length).fill(0));
        animateIndex(0);
      } else {
        // Skip animation and set directly to targets
        setWidths(targets);
      }
    }
    return () => { cancelled = true; };
  }, [dailyQuests.length, targets, shouldAnimate]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const txt = hours > 0 ? `${hours} ساعت` : `${mins} دقیقه`;
    return toPersianDigits(txt);
  };

  const getChestColor = (index: number) => {
    const colors = [
      'from-purple-400 to-purple-600',
      'from-indigo-400 to-indigo-600',
      'from-blue-400 to-blue-600',
    ];
    return colors[index % colors.length];
  };

  const handleQuestClick = (quest: DailyQuest) => {
    if (quest.isCompleted) return;
    navigate('/learning');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div>
          <div className="animate-spin rounded-full h-12 w-12 md:h-14 md:w-14 border-b-2 border-[#7063ff] mx-auto mb-4"></div>
          <p className="text-slate-700 font-persian text-sm md:text-base">در حال بارگذاری ماموریت‌ها...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-24 lg:pb-28" dir="rtl">
      {/* Header */}
      <div className="bg-[#ffffff] px-4 md:px-6 lg:px-8 py-3 md:py-4 mb-6 md:mb-7 flex items-center justify-between sticky top-0 z-50 mt-0.5">
        <div className="flex-1 flex items-center gap-1 md:gap-2">
          <h1 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">چالش‌های روزانه</h1>
          {dailyQuests.length > 0 && (
            <div className="mr-auto flex items-center gap-1 md:gap-2 text-[#fea14c]">
              <Clock className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-bold text-sm md:text-base lg:text-lg leading-none">{formatTime(timeRemaining)}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-105"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
      </div>

      <div className="max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto p-5 md:p-6 lg:p-8">


        {/* White card like ChallengesPage (compact) */}
        <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-2xl border border-gray-100">
          <div className="space-y-8 md:space-y-10 lg:space-y-12">
            {dailyQuests.length === 0 ? (
              <div className="py-12 md:py-16 lg:py-20 text-center">
                <p className="text-[#2d3747] text-base md:text-lg lg:text-xl font-bold">برای امروز چالشی وجود ندارد ✨</p>
              </div>
            ) : (
              dailyQuests.slice(0, 3).map((quest, index) => {
                const percent = Math.round(widths[index] || 0);
                return (
                  <div key={quest.id} className="group cursor-pointer transition-all duration-200 hover:scale-[1.02]" onClick={() => handleQuestClick(quest)}>
                    <div>
                      <h2 className="text-[#2d3747] text-[15px] md:text-base lg:text-lg font-bold mb-3 md:mb-4">{toPersianDigits(quest.title)}</h2>
                      <div className="flex items-center">
                        <div className="flex-1 ml-1.5 md:ml-2 h-5 md:h-6 lg:h-7 rounded-r-full overflow-hidden relative shadow-inner" style={{ backgroundColor: '#f3f4f6' }}>
                          {/* progress bar (plain div) */}
                          <div className="absolute right-0 top-0 bottom-0 rounded-r-full" style={{ backgroundColor: '#fea14c', width: `${percent}%`, transition: 'none' }} />
                          {/* centered text */}
                          <div className="absolute inset-0 z-10 grid place-items-center pointer-events-none">
                            <span className={`text-xs md:text-sm lg:text-base font-semibold ${percent >= 50 ? 'text-white' : 'text-[#2c374d]'}`}>
                              {toPersianNumber(quest.target)} / {toPersianNumber(quest.progress)}
                            </span>
                          </div>
                        </div>
                        <div className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-br ${getChestColor(index)} rounded-xl flex items-center justify-center shadow-lg border-2 border-white/20 transition-all duration-200 group-hover:scale-110`}>
                          <div className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9">
                            <ChestIcon />
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < dailyQuests.slice(0, 3).length - 1 && (
                      <div className="border-t border-gray-200 mt-6 md:mt-8 lg:mt-10 -mx-6 md:-mx-8 lg:-mx-10" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyQuests;